/**
 * InsightsBanner — Dating Insights nudge (Feb 2026)
 *
 * Renders the highest-priority active nudge from GET /api/insights/nudges
 * at the top of Discover. Dismissing snoozes the nudge for 7 days via
 * POST /api/insights/nudges/{key}/dismiss.
 *
 * Warm nudges (women — swipe more / reply more) use a rose accent.
 * Focus nudges (men — swipe less) use an indigo accent.
 *
 * User-facing label is "Dating Insights" — never mention gender in copy.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, MessageCircle, Target, X, ChevronRight } from 'lucide-react';
import { api } from '@/contexts/AuthContext';

const KIND_STYLE = {
  warm: {
    icon: Sparkles,
    accent: '#B91C1C',
    bg: 'linear-gradient(135deg, rgba(244,63,94,0.08), rgba(220,38,38,0.05))',
    ring: 'rgba(220,38,38,0.16)',
  },
  focus: {
    icon: Target,
    accent: '#1E4FD8',
    bg: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(30,79,216,0.05))',
    ring: 'rgba(30,79,216,0.16)',
  },
};

// Some nudges override the default kind icon based on the underlying stat
// (e.g. "reply_more" always shows a message icon regardless of kind).
const KEY_ICON = {
  reply_more: MessageCircle,
  swipe_open_up: Sparkles,
  swipe_be_intentional: Target,
};

export default function InsightsBanner({ onNudge }) {
  const navigate = useNavigate();
  const [nudge, setNudge] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get('/insights/nudges').then((r) => {
      if (cancel) return;
      const list = r.data?.nudges || [];
      setNudge(list[0] || null);
      // Let the parent know the current top nudge so Profile stat tiles
      // can also react (e.g. swap the "% Right" tile into a coach CTA).
      if (onNudge) onNudge(list[0] || null);
    }).catch(() => { /* non-fatal */ });
    return () => { cancel = true; };
  }, [onNudge]);

  if (!nudge) return null;
  const style = KIND_STYLE[nudge.kind] || KIND_STYLE.warm;
  const Icon = KEY_ICON[nudge.key] || style.icon;

  const dismiss = async (e) => {
    e.stopPropagation();
    setDismissing(true);
    // Fire-and-forget analytics BEFORE the snooze call so we always log
    // the intent even if the snooze POST 5xxs.
    api.post(`/insights/nudges/${nudge.key}/event`, { event: 'dismissed' }).catch(() => {});
    try {
      await api.post(`/insights/nudges/${nudge.key}/dismiss`);
    } catch { /* keep the banner up if the snooze failed */ }
    setNudge(null);
    if (onNudge) onNudge(null);
  };

  const go = () => {
    if (!nudge.cta_href) return;
    api.post(`/insights/nudges/${nudge.key}/event`, { event: 'tapped' }).catch(() => {});
    navigate(nudge.cta_href);
  };

  return (
    <div
      className="mx-4 mt-3 rounded-2xl px-4 py-3 flex items-start gap-3 cursor-pointer transition-shadow hover:shadow-[0_4px_14px_rgba(15,15,16,0.06)]"
      style={{ background: style.bg, border: `1px solid ${style.ring}` }}
      onClick={go}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') go(); }}
      data-testid="insights-banner"
      data-nudge-key={nudge.key}
    >
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: '#FFFFFF', border: `1px solid ${style.ring}` }}
      >
        <Icon size={16} style={{ color: style.accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span
            className="text-[10px] font-[800] tracking-[0.14em] uppercase"
            style={{ color: style.accent }}
          >
            Dating Insights
          </span>
        </div>
        <h4 className="text-[14.5px] font-[800] tracking-[-0.2px] leading-snug text-[#0F0F10] mt-0.5">
          {nudge.title}
        </h4>
        <p className="text-[12.5px] leading-snug text-[#4B4B52] mt-1">
          {nudge.body}
        </p>
        {nudge.cta_label && (
          <div
            className="inline-flex items-center gap-0.5 mt-2 text-[12px] font-[800] tracking-[-0.1px]"
            style={{ color: style.accent }}
            data-testid="insights-banner-cta"
          >
            {nudge.cta_label} <ChevronRight size={14} />
          </div>
        )}
      </div>
      <button
        onClick={dismiss}
        disabled={dismissing}
        aria-label="Dismiss nudge"
        data-testid="insights-banner-dismiss"
        className="w-7 h-7 rounded-full flex items-center justify-center text-[#8A8A93] hover:text-[#0F0F10] hover:bg-black/[0.04] transition-colors flex-shrink-0"
      >
        <X size={15} />
      </button>
    </div>
  );
}
